import { useState } from 'react';
import {
  Avatar,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Switch,
} from '@nextui-org/react';
import { cn } from 'common/src/utils/cn';
import { useLanguageStore } from 'common/src/stores/useLanguageStore';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import { type Language } from '../../../../../common/src/i18n/types';
import { useTheme } from '../../../hooks/useTheme';
import { SidebarIcon as Icon } from './SidebarIcon';

interface ProfileMenuProps {
  isCompact?: boolean;
  onLogout: () => void;
  /** Solo se pasa si el usuario aún no activó la verificación en dos pasos. */
  onOpenTwoFactor?: () => void;
}

// Perfil arriba del menú lateral. Al tocarlo abre las opciones de la cuenta
// (idioma, tema, verificación en dos pasos y cerrar sesión), que antes
// ocupaban el pie del menú.
const ProfileMenu = ({
  isCompact = false,
  onLogout,
  onOpenTwoFactor,
}: ProfileMenuProps) => {
  const { userProfile } = useUserStore();
  const { language, setLanguage } = useLanguageStore();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useTranslation('sidebar');
  // Abierto a mano: idioma y tema cambian sin cerrar el menú.
  const [isOpen, setIsOpen] = useState(false);

  const name = [userProfile?.first_name, userProfile?.last_name]
    .filter(Boolean)
    .join(' ');
  const initials =
    (userProfile?.first_name?.[0] || '') + (userProfile?.last_name?.[0] || '');
  const logoUrl = userProfile?.exporter?.logo_url;

  const onAction = (key: React.Key) => {
    const action = String(key);
    if (action === 'language') {
      setLanguage(language === 'es' ? 'en' : 'es');
    } else if (action === 'theme') {
      toggleTheme();
    } else if (action === 'two-factor') {
      setIsOpen(false);
      onOpenTwoFactor?.();
    } else if (action === 'logout') {
      setIsOpen(false);
      onLogout();
    }
  };

  // Idioma y tema en una fila cada uno, con el control a la derecha.
  const settingsItems = [
    <DropdownItem
      key='language'
      startContent={
        <Icon className='text-default-500' icon='tabler:language' width={20} />
      }
      endContent={
        <span className='flex rounded-small bg-default-100 p-0.5 text-tiny font-medium'>
          {(['es', 'en'] as Language[]).map((code) => (
            <span
              key={code}
              className={cn('rounded-small px-1.5 py-0.5 text-default-500', {
                'bg-background text-foreground shadow-small': language === code,
              })}
            >
              {code.toUpperCase()}
            </span>
          ))}
        </span>
      }
    >
      {t('Idioma')}
    </DropdownItem>,
    <DropdownItem
      key='theme'
      startContent={
        <Icon
          className='text-default-500'
          icon={isDark ? 'tabler:moon' : 'tabler:sun'}
          width={20}
        />
      }
      endContent={
        // Solo muestra el estado: la fila completa es la que cambia el tema.
        <Switch
          aria-hidden
          size='sm'
          isSelected={isDark}
          className='pointer-events-none'
        />
      }
    >
      {t('Modo Oscuro')}
    </DropdownItem>,
    ...(onOpenTwoFactor
      ? [
          <DropdownItem
            key='two-factor'
            startContent={
              <Icon
                className='text-default-500'
                icon='tabler:shield-lock'
                width={20}
              />
            }
          >
            {t('Verificación en dos pasos')}
          </DropdownItem>,
        ]
      : []),
  ];

  return (
    // Mismo ancho que el perfil (el menú lateral mide 240 px de contenido).
    <Dropdown
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement={isCompact ? 'right-start' : 'bottom-start'}
      classNames={{ content: 'min-w-60' }}
    >
      <DropdownTrigger>
        <button
          type='button'
          aria-label={t('Cuenta')}
          className={cn(
            'flex w-full items-center gap-3 rounded-medium px-3 py-2 text-left outline-none transition-colors hover:bg-default-100 data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-focus',
            { 'justify-center px-0': isCompact }
          )}
        >
          {logoUrl ? (
            <span className='flex h-10 w-10 flex-none items-center justify-center overflow-hidden rounded-full border-1 border-default bg-background'>
              <img
                src={logoUrl}
                alt={userProfile?.exporter?.name || ''}
                className='max-h-full max-w-full object-contain'
              />
            </span>
          ) : (
            <Avatar isBordered className='flex-none' size='sm' name={initials} />
          )}
          {isCompact ? null : (
            <>
              <span className='flex min-w-0 flex-1 flex-col'>
                <span className='truncate text-small font-medium text-default-600'>
                  {name}
                </span>
                <span className='truncate text-tiny text-default-400'>
                  {userProfile?.exporter?.name || userProfile?.roles?.[0]}
                </span>
              </span>
              <Icon
                className='flex-none text-default-400'
                icon='tabler:selector'
                width={18}
              />
            </>
          )}
        </button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label={t('Cuenta')}
        closeOnSelect={false}
        onAction={onAction}
      >
        <DropdownSection showDivider>{settingsItems}</DropdownSection>
        <DropdownSection>
          <DropdownItem
            key='logout'
            startContent={
              <Icon
                className='rotate-180 text-default-500'
                icon='solar:logout-line-duotone'
                width={20}
              />
            }
          >
            {t('Cerrar sesión')}
          </DropdownItem>
        </DropdownSection>
      </DropdownMenu>
    </Dropdown>
  );
};

export default ProfileMenu;
